import React, { useEffect, useState } from "react";
import theme from '../../shared/theme';
import { makeStyles, ThemeProvider } from '@material-ui/core/styles';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, Grid, LinearProgress, MenuItem, Select, TextField, Typography } from "@material-ui/core";
import {
    Clear as ClearIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@material-ui/icons';
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { prepareLocalStateReScheduleToSaveBackend } from "../planner/virtualTripHelpers";
import DateFnsUtils from '@date-io/date-fns';
import api from "../../../store/api";
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import { Alert } from "@material-ui/lab";

export interface RescheduledModalData {
    approvedBy: string;
    transferId: string;
    firstName: string;
    lastName: string;
    previousStartTrip: any;
    startTrip: any;
    reason: any;
  }

const ReschedulingApprovalPopup = (props: any): JSX.Element => {
    const { open, onClose, onSave, rescheduleReason, setLoading, loading, onReset } = props
    const classes = useStyles();
    let dateUtils = new DateFnsUtils();
    const { handleSubmit, control, formState, errors, getValues } = useForm();
    const [reScheduleModelData, SetReScheduledModelData] = React.useState<RescheduledModalData[]>([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [disableButton, setDisableButton] = useState(false);

    useEffect(() => {
        if(open){
            const modelData = prepareLocalStateReScheduleToSaveBackend()
            SetReScheduledModelData(modelData)
        }

        return () => {
            SetReScheduledModelData([]);
        }
    }, [open,SetReScheduledModelData])
    

    const onSubmit = async () => {
        const approvedBy = getValues("approvedBy");
        const dateUtils = new DateFnsUtils();
        const updatedList = reScheduleModelData.map(item => {
            return { ...item , approvedBy: approvedBy
                ,previousStartTrip : item.previousStartTrip != '' ? dateUtils.format(item.previousStartTrip, "yyyy-MM-dd HH:mm:ss") : null
                ,startTrip : item.startTrip != '' ? dateUtils.format(item.startTrip, "yyyy-MM-dd HH:mm:ss") : null};
          });

        try {
            setLoading(true);
            setDisableButton(true);
            setErrorMessage("");

            const res = await api.UpdateRescheduledReasonOnSaveChanges("", updatedList);
            if (!res.hasError) {
                onSave();
            }
            else {
                setErrorMessage(res?.message)
                setDisableButton(false);
                setLoading(false);
            }
        } catch (error) {
            setErrorMessage("An error has occurred. Please try again.");
            setDisableButton(false);
            setLoading(false);
        }
    }
    
    const handleClose = () => {
        onReset()
        onClose()
    }

    const handleOnChange = (transferId:any,newReason:any) => {
        const updatedList = reScheduleModelData.map(item => {
            if (item.transferId === transferId) {
              return { ...item , reason: newReason };
            }
            return item;
          });
      
          SetReScheduledModelData(updatedList)
    }

    return (
        <ThemeProvider theme={theme}>
            {open && (
                <Dialog
                    open={open}
                    onClose={handleClose}
                    classes={{ paper: classes.dialogPaper }}
                    fullWidth
                >
                    <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                        <DialogTitle className={classes.dialogTitle} disableTypography>
                            Rescheduling Approval
                            <ClearIcon className="closeIcon" onClick={handleClose} />
                        </DialogTitle>
                        <DialogContent className={classes.dialogContent}>
                            <Box className={"formContainer"}>
                                <Grid container className="nameSection">
                                    <Grid item xs={4}>
                                        <Typography component="div" className={classes.formLabel}>
                                            Changes approved by
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={8}>
                                        <Controller
                                            control={control}
                                            name="approvedBy"
                                            defaultValue={""}
                                            rules={{
                                                required: "Approved by is required",
                                                validate: (value) => {
                                                  if (value.trim() === "") {
                                                    return "Approved by cannot be empty";
                                                  }
                                                  return true;
                                                },
                                              }}
                                            render={(props) => (
                                                <TextField
                                                    {...props}
                                                    fullWidth
                                                    variant='outlined'
                                                    InputProps={{ classes: { root: classes.textFieldInput } }}
                                                    placeholder="Name of customer approver"
                                                    error={Boolean(errors.approvedBy)}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    {errors.approvedBy && (
                                    <Grid item xs={12}>
                                        <FormHelperText error={Boolean(errors.approvedBy)}>
                                            {errors.approvedBy ? errors.approvedBy.message : ''}
                                        </FormHelperText>
                                    </Grid>
                                    )}
                                </Grid>
                                <Box className={classes.divider}></Box>
                                <Box className="reasonSection">
                                    <Box className="labelBox">
                                        <Typography component="div" className={classes.formLabel}>
                                            Reason for rescheduling
                                        </Typography>
                                    </Box>
                                    <Grid container className="listBox">
                                        {reScheduleModelData.map((x) => (
                                            <Grid key={x.transferId} item xs={12} className="listItem">   
                                                <Box className="header">
                                                    <Typography component="div" className="title">
                                                        {x.firstName + ' ' + x.lastName}
                                                    </Typography>
                                                    <Typography component="div" className="time">
                                                        From {dateUtils.format(new Date(x.previousStartTrip), "HH:mm")} to {dateUtils.format(new Date(x.startTrip), "HH:mm")}
                                                    </Typography>
                                                </Box>
                                                <Box className="content">
                                                    <Controller
                                                        control={control}
                                                        name={`reason-${x.transferId}`}
                                                        defaultValue={""}
                                                        rules={{
                                                            required: "The field is required"
                                                        }}
                                                        render={(props) => (
                                                            <FormControl fullWidth error={Boolean(errors[`reason-${x.transferId}`])}>
                                                                <Select
                                                                    {...props}
                                                                    onChange={(e: any) => {
                                                                        props.onChange(e);
                                                                        handleOnChange(x.transferId,e?.target?.value);
                                                                    }}
                                                                    fullWidth
                                                                    variant="outlined"
                                                                    className={classes.selectField}
                                                                    IconComponent={KeyboardArrowDownIcon}
                                                                    displayEmpty
                                                                >
                                                                    <MenuItem value="">Choose reason why this transfer was rescheduled</MenuItem >
                                                                    {rescheduleReason.map((item: any) => {
                                                                        return (
                                                                            <MenuItem selected={item.selected} key={item.reasonForRescheduleId} value={item.reasonForRescheduleId}>
                                                                                {item.name}
                                                                            </MenuItem>
                                                                        );
                                                                    })}
                                                                </Select>
                                                                <FormHelperText error={Boolean(errors[`reason-${x.transferId}`])}>
                                                                    {errors[`reason-${x.transferId}`] ? errors[`reason-${x.transferId}`].message : ""}
                                                                </FormHelperText>
                                                            </FormControl>
                                                        )}
                                                    />
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            </Box>
                        </DialogContent>
                        {errorMessage && (<div style={{ paddingLeft: 25, paddingRight: 25, }}>
                            <div className={classes.headingAlert}>
                                <Alert variant="filled" icon={<ReportProblemOutlinedIcon />} severity="error" classes={{ root: classes.alertRoot }} style={{ display: errorMessage ? 'flex' : 'none' }}>
                                    {errorMessage}
                                </Alert>
                            </div>
                        </div>)}

                        {loading && (<div style={{ paddingTop: 10, paddingBottom: 10 }}>
                            {loading ? <CreateLoadingLine /> : null}
                        </div>)}
                        <DialogActions className={classes.dialogActions}>
                            <Grid container spacing={3}>
                                <Grid item xs={6}>
                                    <Button type="button" className={`${classes.button} cancelBtn`} disabled={disableButton} onClick={handleClose}>
                                        Cancel
                                    </Button>
                                </Grid>
                                <Grid item xs={6}>
                                    <Button type="submit" className={`${classes.button} submitBtn`} disabled={disableButton}>
                                        Done
                                    </Button>
                                </Grid>
                            </Grid>
                        </DialogActions>
                    </form>
                </Dialog>
            )}
        </ThemeProvider>
    )
}

export default ReschedulingApprovalPopup;


const CreateLoadingLine = (props: any): JSX.Element => {
    const classes = useStyles();
    return (
        <div className={classes.loading}>
            <LinearProgress color="primary" />
        </div>
    )
}

const useStyles = makeStyles((theme) => ({
    
    loading: {
        width: '100%',
        '& > * + *': {
            marginTop: theme.spacing(2),
        },
    },
    headingAlert: {
        padding: theme.spacing(0, 0, 2, 0),
    },
    alertRoot: {
        marginTop: 24
    },
    dialogPaper: {
        minWidth: 500,
        margin: 0,
        borderRadius: 0,
        shadow: 'none',
        backgroundColor: '#fff',
    },
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: '#333333',
        fontSize: 16,
        fontWeight: 900,
        padding: '16px 18px',
        '& .closeIcon': {
            height: '20px',
            width: '20px',
            fill: '#00152E',
            cursor: 'pointer',
        },
    },
    dialogContent: {
        padding: '0px',
        //marginBottom: '6px',
        overflowY: 'visible',
        '& .formContainer': {
            '& .nameSection': {
                alignItems: 'center',
                gap: '0px',
                padding: '18px',
                paddingTop: '6px',
            },
            '& .reasonSection': {
                maxHeight: '300px',
                overflowY: 'scroll',
                padding: '18px',
                '& .labelBox': {
                    paddingBottom: '18px',
                },
                '& .listBox': {
                    alignItems: 'center',
                    gap: '18px',
                    '& .listItem': {
                        backgroundColor: '#E8E8E8',
                        borderRadius: '4px',
                        padding: '16px',
                        '& .header': {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            paddingBottom: '16px',
                            '& .title': {
                                color: '#333333',
                                fontSize: '16px',
                                fontWeight: 900,
                            },
                            '& .time': {
                                color: '#333333',
                                fontSize: '13px',
                                fontWeight: 400,
                            },
                        },
                        '& .content': {

                        },
                    },
                },
            },
        },
    },
    dialogActions: {
        padding: '16px 18px',
        paddingTop: '21px',
        borderTop: '1px solid #979797',
    },
    textFieldInput: {
        height: '44px',
        width: '100%',
        backgroundColor: '#E8E8E8',
        color: '#333333',
        fontSize: 16,
        fontWeight: 400,
        borderRadius: '4px',
    },
    selectField: {
        height: "44px",
        width: "100%",
        fontSize: 16,
        fontWeight: 400,
        backgroundColor: '#FFFFFF',
        color: '#333333',
        borderRadius: '4px',
        '& .MuiSelect-selectMenu': {
            fontWeight: 600,
        },
    },
    button: {
        width: '100%',
        fontSize: 18,
        fontWeight: 400,
        borderRadius: '6px',
        padding: '8px',
        '&.cancelBtn': {
            backgroundColor: '#fff',
            color: '#02489D',
            border: '1px solid #02489D',
        },
        '&.submitBtn': {
            backgroundColor: '#02489D',
            color: '#FFFFFF',
        },
    },
    formLabel: {
        color: '#333333',
        fontSize: 16,
        fontWeight: 400,
    },
    divider: {
        borderTop: '1px solid #979797',
    },
}));
